import {
  PricingOptionsDataRoutePath,
  PricingOptionsDataRouteType
} from "routes/data-routes/billing-data/pricing-options/route.meta";
import { ExtractFetcherDataType } from "routes/data-routes/types";
import { useFetcherData } from "v2/hooks/useFetcherData";
import { FetcherKeys } from "routes/dataroutes/Fetchers";
import { useCallback, useEffect, useMemo } from "react";
import { useCallbackAfterSubmission } from "v2/hooks/useCallbackAfterSubmission";
import { useActiveSubscription } from "routes/data-routes/billing-data/active-subscription";


type HookReturnType = ReturnType<NonNullable<PricingOptionsDataRouteType["usePricingOptions"]>>;
type FetcherDataType = ExtractFetcherDataType<HookReturnType>;

export function usePricingOptions(): HookReturnType {
  // pricing options depend on the active subscription, make this hook react to any plan changes
  const { activeSubscription } = useActiveSubscription();

  const {
    value,
    fetcher: pricingOptionsFetcher,
    reload: reloadPricingOptions,
    error
  } = useFetcherData<FetcherDataType>({
    fetcherHref: PricingOptionsDataRoutePath,
    fetcherKey: FetcherKeys.PRICING_OPTIONS + "-" + activeSubscription?.planSlug,
    reloadOnFocus: true,
  });

  useEffect(() => {
    if (pricingOptionsFetcher?.state === "idle") {
      if (!value?.pricingOptions && error) {
        // fetcher preserved an error state and value is lost, have to reload
        reloadPricingOptions();
      }
    }
  }, [ pricingOptionsFetcher?.state, value?.pricingOptions, error, reloadPricingOptions ]);

  const refetchAfterSubmission = useCallback(() => {
    reloadPricingOptions();
  }, [ reloadPricingOptions ]);
  useCallbackAfterSubmission(refetchAfterSubmission, pricingOptionsFetcher);


  const optimisticAutoUpgrade = useMemo(() => {
    const formValue = pricingOptionsFetcher?.formData?.get("auto-upgrade");
    return formValue ? (formValue === "true") : undefined;
  }, [ pricingOptionsFetcher?.formData ]);

  const optimisticPayAsYouGo = useMemo(() => {
    const formValue = pricingOptionsFetcher?.formData?.get("pay-as-you-go");
    return formValue ? (formValue === "true") : undefined;
  }, [ pricingOptionsFetcher?.formData ]);

  // useMemo to avoid returning a new instance every time
  const pricingOptionsValues = useMemo<HookReturnType>(() => {
    return {
      pricingOptions: value?.pricingOptions ? {
        renewal: {
          allowed: value.pricingOptions.renewal.allowed
        },

        "auto-upgrade": {
          allowed: value.pricingOptions["auto-upgrade"].allowed,
          enabled: optimisticAutoUpgrade ?? value.pricingOptions["auto-upgrade"].enabled,
        },

        "pay-as-you-go": {
          allowed: value.pricingOptions["pay-as-you-go"].allowed,
          enabled: optimisticPayAsYouGo ?? value.pricingOptions["pay-as-you-go"].enabled,
          creditsCap: value.pricingOptions["pay-as-you-go"].creditsCap
        }
      } : undefined,

      pricingOptionsFetcher: pricingOptionsFetcher!,
      reloadPricingOptions,
      pricingOptionsError: error
    };
  }, [ value?.pricingOptions, optimisticAutoUpgrade, optimisticPayAsYouGo, pricingOptionsFetcher, reloadPricingOptions, error ]);

  return pricingOptionsValues;
}
